import { RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { InstrumentFreshness, useInstrumentStatus } from '../hooks/useInstrumentStatus';

function formatAge(seconds: number | null): string {
    if (seconds === null) return 'never';
    if (seconds < 60) return `${Math.round(seconds)}s ago`;
    const minutes = seconds / 60;
    if (minutes < 60) return `${Math.round(minutes)}m ago`;
    const hours = minutes / 60;
    if (hours < 48) return `${Math.round(hours)}h ago`;
    const days = hours / 24;
    return `${Math.round(days)}d ago`;
}

const STATUS_LABEL: Record<InstrumentFreshness['status'], string> = {
    fresh: 'Fresh',
    stale: 'Stale',
    very_stale: 'Very stale',
    missing: 'No data',
};

const STATUS_STYLE: Record<InstrumentFreshness['status'], { background: string; color: string }> = {
    fresh: { background: 'var(--success-tint)', color: 'var(--success)' },
    stale: { background: 'var(--accent-tint)', color: 'var(--accent)' },
    very_stale: { background: 'var(--danger-tint)', color: 'var(--danger)' },
    missing: { background: 'var(--background-subtle)', color: 'var(--foreground-muted)' },
};

export default function Home() {
    const { user } = useAuth();
    const { instruments, isLoading, error, refresh } = useInstrumentStatus();

    const freshCount = instruments.filter((s) => s.status === 'fresh').length;
    const staleCount = instruments.length - freshCount;

    return (
        <div className="flex min-h-[calc(100vh-112px)] min-w-0 flex-col gap-4">
            <section className="flex flex-wrap items-center justify-between gap-3 px-1">
                <div>
                    <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">Instrument data freshness</h2>
                    <p className="mt-1 text-sm leading-6 sm:text-base" style={{ color: 'var(--foreground-muted)' }}>
                        Welcome back, {user?.username}. {instruments.length} instruments · {freshCount} fresh · {staleCount} stale or missing
                    </p>
                </div>
                <button
                    type="button"
                    onClick={refresh}
                    disabled={isLoading}
                    className="button-secondary inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium"
                >
                    <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} aria-hidden="true" />
                    Refresh
                </button>
            </section>

            {error ? (
                <section className="surface-card rounded-[28px] px-5 py-5 sm:px-6" role="alert">
                    <p className="text-sm font-medium" style={{ color: 'var(--danger)' }}>
                        {error}
                    </p>
                    <button type="button" onClick={refresh} className="button-secondary mt-4 rounded-full px-4 py-2 text-sm font-medium">
                        Retry
                    </button>
                </section>
            ) : null}

            <div className="surface-card min-h-0 flex-1 rounded-[28px] p-6 sm:p-7">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[560px] border-collapse text-left text-sm">
                        <thead>
                            <tr style={{ color: 'var(--foreground-muted)' }}>
                                <th className="border-b py-2 pr-4 font-medium" style={{ borderColor: 'var(--card-border)' }}>
                                    Instrument
                                </th>
                                <th className="border-b py-2 pr-4 font-medium" style={{ borderColor: 'var(--card-border)' }}>
                                    Source
                                </th>
                                <th className="border-b py-2 pr-4 font-medium" style={{ borderColor: 'var(--card-border)' }}>
                                    Timeframes
                                </th>
                                <th className="border-b py-2 pr-4 font-medium" style={{ borderColor: 'var(--card-border)' }}>
                                    Last update
                                </th>
                                <th className="border-b py-2 font-medium" style={{ borderColor: 'var(--card-border)' }}>
                                    Status
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {instruments.map((row) => (
                                <tr key={row.instrument} className="task-row">
                                    <td className="border-b py-3 pr-4 font-medium" style={{ borderColor: 'var(--card-border)' }}>
                                        {row.instrument}
                                    </td>
                                    <td className="border-b py-3 pr-4" style={{ borderColor: 'var(--card-border)', color: 'var(--foreground-muted)' }}>
                                        {row.feed ?? '—'}
                                    </td>
                                    <td className="border-b py-3 pr-4" style={{ borderColor: 'var(--card-border)', color: 'var(--foreground-muted)' }}>
                                        {row.timeframe_count}
                                    </td>
                                    <td className="border-b py-3 pr-4" style={{ borderColor: 'var(--card-border)', color: 'var(--foreground-muted)' }}>
                                        {formatAge(row.age_seconds)}
                                    </td>
                                    <td className="border-b py-3" style={{ borderColor: 'var(--card-border)' }}>
                                        <span className="inline-flex rounded-full px-3 py-1 text-xs font-semibold" style={STATUS_STYLE[row.status]}>
                                            {STATUS_LABEL[row.status]}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                            {!isLoading && instruments.length === 0 && !error ? (
                                <tr>
                                    <td colSpan={5} className="py-8 text-center" style={{ color: 'var(--foreground-muted)' }}>
                                        No instrument data found.
                                    </td>
                                </tr>
                            ) : null}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
